import React from 'react';
import './pricingCalculatorForm.scss';

import { TYPE_NUMBER } from '@eb/eds-input-field';
import { InputField } from '@eb/eds-input-field';
import { Button } from '@eb/eds-button';
import { AutocompleteField } from '@eb/eds-autocomplete-field';
import { SelectField } from '@eb/eds-input-field';
import { Label } from '@eb/eds-label';

import { Icon } from '@eb/eds-icon';
import { CopyChunky } from '@eb/eds-iconography';
import { ChevronDownChunky } from '@eb/eds-iconography';
import {
    PACKAGE_ONE,
    PACKAGE_TWO,
    DEFAULT_PACKAGE_NAMES,
    PackageTypes,
    ESSENTIALS_LABEL,
    PROFESSIONAL_LABEL,
    TICKET_PRICE_LABEL,
    CHANGE_PLAN_CTA,
    TICKET_QUANTITY_LABEL,
    FEE_HANDLING_TOOLTIP_TEXT,
    PASS_ON_FEES_LABEL,
    PASS_ON_FEES_OPTION,
    PASS_ON_FEES_SECONDARY_TEXT,
    ABSORB_FEES_OPTION,
    ABSORB_FEES_SECONDARY_TEXT,
    PACKAGE_SELECT_LABEL,
    RESULTS_HEADLINE,
    ESTIMATE_TOOLTIP_TEXT,
    RESULTS_CTA,
} from '../../constants/pricing';

import InfoTooltip from '../../sharedComponents/InfoTooltip';
import PriceResults from './PriceResults';

interface PricingCalculatorFormProps {
    assortment: PackageTypes;
    attendeeTotal: string;
    calculateFees: Function;
    currencySymbol: string;
    essentialsDescription: string;
    eventTotal: string;
    header: string;
    impossiblePriceEncountered: boolean;
    isFree: boolean;
    isLoading: boolean;
    openModal: Function;
    passOnFees: boolean;
    professionalDescription: string;
    showChangePlanCTA: boolean;
    ticketPrice: string;
    ticketPriceError?: string;
    ticketQuantity: string;
    ticketQuantityError?: string;
    updateEventState: Function;
    updateTicketPriceState: Function;
}

const PricingCalculatorForm = ({
    assortment,
    attendeeTotal,
    calculateFees,
    currencySymbol,
    essentialsDescription,
    eventTotal,
    header,
    impossiblePriceEncountered,
    isFree,
    isLoading,
    openModal,
    passOnFees,
    professionalDescription,
    showChangePlanCTA,
    ticketPrice,
    ticketPriceError,
    ticketQuantity,
    ticketQuantityError,
    updateEventState,
    updateTicketPriceState,
}: PricingCalculatorFormProps) => {
    const packageSelectOptions = [
        {
            value: PACKAGE_ONE,
            display: ESSENTIALS_LABEL,
        },
        {
            value: PACKAGE_TWO,
            display: PROFESSIONAL_LABEL,
        },
    ];
    const packageDetailText =
        assortment === PACKAGE_ONE
            ? essentialsDescription
            : professionalDescription;

    const changePlanCTA = showChangePlanCTA && (
        <a
            href={'/organizer/change-plan'}
            target="_blank"
            rel="noopener noreferrer"
        >
            <Button style="anchor">{CHANGE_PLAN_CTA}</Button>
        </a>
    );

    const totalContent = isFree ? (
        attendeeTotal
    ) : (
        <div className="pricing-calculator--attendee-total-link">
            <Button onClick={openModal} type="link" style="anchor">
                {attendeeTotal}
            </Button>
        </div>
    );

    return (
        <div
            className={`eds-bg-color--background pricing-calculator ${
                isLoading && 'pricing-calculator--is-loading'
            }`}
        >
            <div className="eds-g-grid marketing-container eds-l-pad-hor-0 eds-l-mn-pad-hor-6 eds-l-md-pad-hor-6 eds-l-sm-pad-hor-4">
                <div className="eds-group">
                    <div className="eds-g-cell eds-g-cell-md-5-12 eds-g-offset-md-2-12 eds-g-cell-1-1 eds-g-cell--has-overflow ">
                        <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-md-10-12 eds-l-mar-bot-6 eds-l-pad-bot-6 eds-l-md-mar-bot-1 eds-l-md-pad-bot-1 eds-g-cell--has-overflow">
                            <div className="pricing-calculator--form-header">
                                <h1 className="eds-l-pad-bot-2">{header}</h1>
                                <div className="eds-g-grid">
                                    <div className="eds-g-cell eds-l-pad-bot-2">
                                        <div
                                            className="html_content"
                                            dangerouslySetInnerHTML={{
                                                __html: packageDetailText,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="eds-l-pad-bot-4 eds-align--space-between eds-group pricing-calculator--large-input-font">
                                <div className="eds-g-cell eds-g-cell-5-12 eds-l-pad-left-1 pricing-calculator--price-input-error-wrapper">
                                    <InputField
                                        id={TICKET_PRICE_LABEL}
                                        borderType="none"
                                        label={TICKET_PRICE_LABEL}
                                        hasError={Boolean(ticketPriceError)}
                                        annotationNote={ticketPriceError}
                                        prefix={currencySymbol}
                                        value={ticketPrice}
                                        onBlur={(value: string | number) => {
                                            updateTicketPriceState(
                                                value,
                                                calculateFees,
                                            );
                                        }}
                                    />
                                </div>
                                <span className="eds-g-cell-2-12 eds-text-hm eds-align--center eds-text--center">
                                    x
                                </span>
                                <div className="eds-g-cell eds-g-cell-5-12 pricing-calculator--transparent-input pricing-calculator--quantity-input-error-wrapper">
                                    <InputField
                                        id={TICKET_QUANTITY_LABEL}
                                        borderType="none"
                                        label={TICKET_QUANTITY_LABEL}
                                        type={TYPE_NUMBER}
                                        hasError={Boolean(ticketQuantityError)}
                                        annotationNote={ticketQuantityError}
                                        value={ticketQuantity}
                                        onChange={(value: string | number) => {
                                            updateEventState(
                                                { ticketQuantity: value },
                                                calculateFees,
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="eds-l-pad-bot-4 pricing-calculator--fee-container eds-align--left eds-align--center-vertical">
                                <div className="pricing-calculator--icon-align">
                                    <InfoTooltip
                                        tooltipId="fee-tooltip"
                                        arrowLocation="left"
                                        text={FEE_HANDLING_TOOLTIP_TEXT}
                                    />
                                </div>
                                <div className="pricing-calculator--fee-container--autocomplete">
                                    <Label htmlFor="feeBreakdown">
                                        <span className="eds-text-bs eds-text-color--grey-600 feeBreakdown-label">
                                            {PASS_ON_FEES_LABEL}
                                        </span>
                                    </Label>
                                    <div className="fake-select--wrapper pricing-calculator--large-input-font">
                                        <AutocompleteField
                                            id="feeBreakdown"
                                            name="feeBreakdown"
                                            suggestions={[
                                                {
                                                    key: PASS_ON_FEES_OPTION,
                                                    value: PASS_ON_FEES_OPTION,
                                                    content: PASS_ON_FEES_OPTION,
                                                    secondaryContent: PASS_ON_FEES_SECONDARY_TEXT,
                                                },
                                                {
                                                    key: ABSORB_FEES_OPTION,
                                                    value: ABSORB_FEES_OPTION,
                                                    content: ABSORB_FEES_OPTION,
                                                    secondaryContent: ABSORB_FEES_SECONDARY_TEXT,
                                                },
                                            ]}
                                            value={
                                                passOnFees
                                                    ? PASS_ON_FEES_OPTION
                                                    : ABSORB_FEES_OPTION
                                            }
                                            onSelect={(
                                                value: string | number,
                                            ) => {
                                                updateEventState(
                                                    {
                                                        passOnFees:
                                                            value ===
                                                            PASS_ON_FEES_OPTION,
                                                    },
                                                    calculateFees,
                                                );
                                            }}
                                            onChange={(
                                                value: string | number,
                                            ) => {
                                                updateEventState(
                                                    {
                                                        passOnFees:
                                                            value ===
                                                            PASS_ON_FEES_OPTION,
                                                    },
                                                    calculateFees,
                                                );
                                            }}
                                            borderType="none"
                                            isLimitedSearch={true}
                                            isV2={true}
                                        />
                                        <div className="fake-select--icon">
                                            <Icon
                                                size="small"
                                                color="ui-700"
                                                type={<ChevronDownChunky />}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="eds-g-cell eds-g-cell-1-1 pricing-calculator--large-select-font pricing-calculator--package-select-container">
                                <div className="eds-align--left eds-align--center-vertical">
                                    <div className="pricing-calculator--package-icon pricing-calculator--icon-align">
                                        <Icon
                                            size="xsmall"
                                            color="ui-700"
                                            type={<CopyChunky />}
                                        />
                                    </div>
                                    <div className="pricing-calculator--package-select">
                                        <SelectField
                                            id="package-select"
                                            label={PACKAGE_SELECT_LABEL}
                                            borderType="none"
                                            values={packageSelectOptions}
                                            style="basic"
                                            value={assortment}
                                            onChange={(
                                                value: string | number,
                                            ) => {
                                                updateEventState(
                                                    { assortment: value },
                                                    calculateFees,
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="eds-g-cell eds-g-cell-md-4-12 eds-g-cell-1-1 pricing-calculator--results">
                        <h2 className="eds-align--center eds-l-pad-bot-2">
                            {RESULTS_HEADLINE}
                            <InfoTooltip
                                tooltipId="heading-tooltip"
                                arrowLocation="top"
                                superscript={true}
                                text={ESTIMATE_TOOLTIP_TEXT}
                            />
                        </h2>
                        <PriceResults
                            eventTotal={eventTotal}
                            impossiblePriceEncountered={
                                impossiblePriceEncountered
                            }
                            isFree={isFree}
                            totalContent={totalContent}
                            isLoading={isLoading}
                        />
                        <div className="eds-align--center">
                            <a
                                href={`/signin/?referrer=%2Fcreate&pckg=${DEFAULT_PACKAGE_NAMES[
                                    assortment
                                ].toLowerCase()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button style="neutral">{RESULTS_CTA}</Button>
                            </a>
                        </div>
                        <div className="eds-align--center pricing-calculator--change-plan-cta">
                            {changePlanCTA}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingCalculatorForm;
